import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, ChevronRight } from "lucide-react";
import { NAV_LINKS, CONTACT } from "@/data/site";

function Logo() {
  return (
    <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group">
      <span className="relative grid h-9 w-9 place-items-center rounded-xl bg-dq-ink text-white font-display font-extrabold text-lg transition-transform duration-300 group-hover:scale-105">
        D
        <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-dq-orange ring-2 ring-white" />
      </span>
      <span className="font-display text-[17px] font-extrabold tracking-tight text-dq-ink">
        DiziTeQ
      </span>
    </Link>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-[padding] duration-300 ${
        scrolled ? "py-2.5" : "py-4"
      }`}
    >
      <div className="dq-container">
        <div
          className={`flex items-center justify-between rounded-full px-4 py-2.5 transition-[background,box-shadow,border-color] duration-300 ${
            scrolled
              ? "glass dq-shadow-soft"
              : "border border-transparent bg-transparent"
          }`}
        >
          <Logo />

          <nav className="hidden items-center gap-1 xl:flex">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `rounded-full px-3.5 py-2 text-[14px] font-medium transition-colors duration-200 ${
                    isActive
                      ? "text-dq-orange"
                      : "text-dq-muted hover:text-dq-ink"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <a
              href={`tel:${CONTACT.phoneRaw}`}
              data-testid="nav-phone"
              className="hidden items-center gap-2 rounded-full border border-dq-line bg-white px-4 py-2 text-sm font-semibold text-dq-ink transition-colors hover:border-dq-ink lg:inline-flex"
            >
              <Phone className="h-4 w-4 text-dq-orange" />
              Call
            </a>
            <Link to="/contact" data-testid="nav-cta" className="btn-primary hidden py-2.5 text-sm sm:inline-flex">
              Get Free Consultation
            </Link>
            <button
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle menu"
              className="grid h-10 w-10 place-items-center rounded-full border border-dq-line bg-white xl:hidden"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="dq-container xl:hidden"
          >
            <div className="glass dq-shadow-soft mt-2 rounded-3xl p-3">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={({ isActive }) =>
                    `flex items-center justify-between rounded-2xl px-4 py-3 text-[15px] font-medium ${
                      isActive ? "bg-dq-orange/10 text-dq-orange" : "text-dq-ink"
                    }`
                  }
                >
                  {l.label}
                  <ChevronRight className="h-4 w-4 opacity-40" />
                </NavLink>
              ))}
              <Link to="/contact" data-testid="mobile-cta" className="btn-primary mt-2 w-full">
                Get Free Consultation
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
